import React from 'react';
import PropTypes from 'prop-types';
import qs from 'query-string';
import pluralize from 'pluralize';
import withStyles from '@material-ui/core/styles/withStyles';
import Button from '@material-ui/core/Button';
import FormControl from '@material-ui/core/FormControl';
import Input from '@material-ui/core/Input';
import InputLabel from '@material-ui/core/InputLabel';
import Typography from '@material-ui/core/Typography';
import SplitPageContainer from 'components/SplitPageContainer';
import SplitPageRight from 'components/SplitPageRight';

const TRY_LIMIT = 5;
const LOCAL_STORAGE_LOCKOUT_KEY = 'usernameLockoutTimestamp';
const TRY_LIMIT_REACHED_ERROR =
  'Too many failed login attempts. You may try again after 5 minutes.';

const styles = theme => {
  return {
    header: {
      color: theme.palette.primary.dark,
      fontSize: 50.9,
      [theme.breakpoints.down('xs')]: {
        marginTop: theme.spacing(4),
      },
    },
    subheader: {
      fontSize: 17,
      letterSpacing: 0.2,
      lineHeight: 1.41,
      color: theme.palette.common.black,
    },
    input: {
      display: 'block',
      maxWidth: 400,
      minHeight: 70,
      marginTop: theme.spacing(6),
      [theme.breakpoints.down('sm')]: {
        marginLeft: 'auto',
        marginRight: 'auto',
      },
    },
    labelSize: {
      fontSize: 25,
      paddingTop: theme.spacing(1),
    },
    inputSize: {
      fontSize: 28,
      width: '100%',
      paddingTop: theme.spacing(1),
    },
    buttonContainer: {
      marginTop: theme.spacing(8),
    },
    button: {
      width: 133,
      height: 39,
      fontSize: 14.6,
      fontWeight: 'bold',
      lineHeight: 1.1,
      letterSpacing: 1.3,
      transition: 'none',
    },
    disabled: {
      borderColor: theme.palette.grey[300],
    },
    error: {
      marginTop: theme.spacing(2),
      color: theme.palette.error.main,
      transition: 'opacity .3s',
      fontSize: 15,
    },
  };
};

class WorkEmail extends React.PureComponent {
  static propTypes = {
    classes: PropTypes.object.isRequired,
    fetchLoginOption: PropTypes.func.isRequired,
    preserveRedirectQuery: PropTypes.func.isRequired,
    error: PropTypes.string.isRequired,
    loginDomains: PropTypes.array.isRequired,
    status: PropTypes.object.isRequired,
    location: PropTypes.object.isRequired,
    history: PropTypes.object.isRequired,
  };

  state = {
    email: '',
    errorState: false,
    errorMessage: '',
    tryCount: 0,
  };

  componentDidMount() {
    if (window.location.pathname.length > 1) {
      this.props.history.push('/');
    }

    this.props.preserveRedirectQuery(
      qs.parse(this.props.location.search).redirectTo,
      this.props.location.search,
    );
  }

  componentDidUpdate(prevProps, prevState) {
    const { loginDomains, history } = this.props;
    if (loginDomains !== prevProps.loginDomains) {
      if (loginDomains.length === 1) {
        history.push(`/sign-in/0`);
      } else if (loginDomains.length > 1) {
        history.push(`/domain`);
      }
    }
    if (!prevProps.status.hasError() && this.props.status.hasError()) {
      this.updateError();
    }
    this.setErrorState(prevProps, prevState);
  }

  setErrorState = (prevProps, prevState) => {
    if (prevProps !== undefined && prevProps.status.isLoading() && this.props.status.hasError()) {
      this.setState({ errorState: true });
    } else if (prevState !== undefined && prevState.email !== this.state.email) {
      this.setState({ errorState: false });
      this.setState({ errorMessage: '' });
    }
  };

  updateError = () => {
    if (this.emailInput) {
      this.emailInput.select();
    }
  };

  handleChange = event => {
    this.setState({ email: event.target.value });
  };

  fetchLoginOptions = () => {
    let currentTryCount = this.state.tryCount;
    const currentLockoutTimestamp = localStorage.getItem(LOCAL_STORAGE_LOCKOUT_KEY);
    const fiveMinutesMS = 1000 * 60 * 5;

    if (currentLockoutTimestamp) {
      if (parseInt(currentLockoutTimestamp) + fiveMinutesMS > Date.now()) {
        this.setState({
          errorMessage: TRY_LIMIT_REACHED_ERROR,
        });
        this.setState({ errorState: true });

        return;
      } else {
        currentTryCount = 0;
        localStorage.removeItem(LOCAL_STORAGE_LOCKOUT_KEY);
      }
    }

    this.props.fetchLoginOption(this.state.email);

    if (currentTryCount >= TRY_LIMIT - 1) {
      localStorage.setItem(LOCAL_STORAGE_LOCKOUT_KEY, Date.now());
      this.setState({
        errorMessage: TRY_LIMIT_REACHED_ERROR,
      });
      this.setState({ errorState: true });
      this.setState({ tryCount: currentTryCount + 1 });
      return;
    }

    this.setState({ tryCount: currentTryCount + 1 });
  };

  isValidEmail = email => {
    const valid = /^\S+@\S+$/;
    return valid.test(String(email).toLowerCase());
  };

  handleSubmit = event => {
    if (this.isValidEmail(this.state.email)) {
      this.fetchLoginOptions();
    }

    event.preventDefault();
  };
  handleKeyPress = event => {
    if (event.key === 'Enter') {
      this.handleSubmit(event);
    }
  };

  displayError = () => {
    const { classes, status } = this.props;
    const { errorState, errorMessage, tryCount } = this.state;
    const errorFromAPI = this.props.error || '';
    const remainingTries = TRY_LIMIT - tryCount;

    const renderErrorMsg = () => {
      if (errorFromAPI) {
        if (tryCount >= TRY_LIMIT) {
          return errorMessage;
        } else if (remainingTries <= 2) {
          const attempts = pluralize('attempt', remainingTries);
          return `${errorFromAPI}. You have ${remainingTries} ${attempts} remaining until you are locked out for 5 minutes.`;
        } else {
          return errorFromAPI;
        }
      } else {
        return errorMessage;
      }
    };

    return (
      <>
        {!status.isLoading() && (
          <Typography
            className={classes.error}
            style={{
              opacity: errorState ? 1 : 0,
              visibility: errorState ? 'visible' : 'hidden',
            }}
          >
            {renderErrorMsg()}
          </Typography>
        )}
      </>
    );
  };
  render() {
    const { classes, status } = this.props;

    return (
      <SplitPageContainer>
        <SplitPageRight>
          <Typography className={classes.header} variant="h2">
            Sign In
          </Typography>
          <Typography className={classes.subheader}>Enter your work email</Typography>
          <FormControl className={classes.input}>
            <InputLabel className={classes.labelSize}>Your Work Email</InputLabel>
            <Input
              className={classes.inputSize}
              onChange={this.handleChange}
              inputRef={input => {
                this.emailInput = input;
              }}
              autoFocus
              value={this.state.email}
              onKeyPress={this.handleKeyPress}
            />
          </FormControl>
          {this.displayError()}
          <div className={classes.buttonContainer}>
            <Button
              className={classes.button}
              classes={{ disabled: classes.disabled }}
              onClick={this.fetchLoginOptions}
              itemType="submit"
              color="secondary"
              variant="contained"
              disabled={!this.isValidEmail(this.state.email) || status.isLoading()}
            >
              {status.isLoading() ? 'Loading' : 'Continue'}
            </Button>
          </div>
        </SplitPageRight>
      </SplitPageContainer>
    );
  }
}

export default withStyles(styles)(WorkEmail);
